'use client'

import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * A square, ruled table (DESIGN.md "Data tables").
 *
 * Rows are `--row-height` (40px) and ruled with
 * `--border-subtle`; column heads are 38px, 12px weight 500 in
 * `--muted-foreground` over a `--border` rule. Text is `--grid-font-size`.
 * Hover is `--accent`; a selected row (`data-state="selected"`) is grey ink,
 * `--brand-subtle`, with no edge marker, and keeps its fill under the pointer.
 *
 * The first and last cells pad themselves by `--table-edge` (12px by
 * default). A `Panel` sets it to the bleed, so a flush table's text aligns to
 * the page title's edge while its rules overhang it.
 *
 * `stickyHeader` keeps the column heads on screen while the rows scroll. The
 * container is a scroll port whether or not anyone asked for one
 * (`overflow-x: auto` makes `overflow-y` compute to `auto`), and its height
 * grows with the table, so a sticky head has nothing to stick to. Bounding the
 * box makes it the thing that scrolls. It is opt-in, because a bounded box is
 * wrong for a short table. The head is painted `--card` so scrolled rows do
 * not read through it, and its rule is an inset shadow because a collapsed
 * border belongs to the table and would scroll away. Print takes the cap off.
 */
function Table({
  className,
  stickyHeader = false,
  ...props
}: React.ComponentProps<'table'> & { stickyHeader?: boolean }) {
  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto',
        stickyHeader && [
          'max-h-[70vh] overflow-auto',
          '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-card',
          '[&_thead_th]:shadow-[inset_0_-1px_0_var(--color-border)]',
          'print:max-h-none print:overflow-visible',
        ],
      )}
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom border-collapse text-grid', className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        '[&_tr]:h-[38px] [&_tr]:border-b-border [&_tr]:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('animate-enter-stagger [&_tr:last-child]:border-b-0', className)}
      {...props}
    />
  )
}

/** Totals under the rows: a `--line-strong` rule, weight 500. */
function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'border-t border-line-strong font-medium [&_tr]:border-b-0 [&_tr]:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'h-row border-b border-border-subtle transition-ui hover:bg-accent',
        // `hover:bg-accent` is a variant and outranks a plain `bg-*`, so the
        // selection re-declares itself under the pointer or it would vanish.
        'data-[state=selected]:bg-brand-subtle data-[state=selected]:text-brand-subtle-foreground data-[state=selected]:hover:bg-brand-subtle',
        'focus-visible:-outline-offset-[3px]',
        className,
      )}
      {...props}
    />
  )
}

const EDGE_PADDING = 'px-3 first:ps-[var(--table-edge,0.75rem)] last:pe-[var(--table-edge,0.75rem)]'

/**
 * A column head. `numeric` end-aligns it with its figures, using the logical
 * property, so a money column lines up under its head in both languages.
 */
function TableHead({
  className,
  numeric = false,
  ...props
}: React.ComponentProps<'th'> & { numeric?: boolean }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        EDGE_PADDING,
        'h-[38px] align-middle text-xs font-medium whitespace-nowrap text-muted-foreground',
        numeric ? 'text-end' : 'text-start',
        className,
      )}
      {...props}
    />
  )
}

/**
 * A cell. `numeric` end-aligns a figure; tabular figures are inherited from
 * the body ("The Global Figures Rule"), so nothing here resets them.
 */
function TableCell({
  className,
  numeric = false,
  ...props
}: React.ComponentProps<'td'> & { numeric?: boolean }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        EDGE_PADDING,
        'py-1.5 align-middle whitespace-nowrap',
        numeric && 'text-end',
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-3 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

/**
 * A formatted figure: Western digits in a left-to-right isolate, so the sign
 * and the digits keep their order inside Arabic text, and never wrapped.
 *
 * `negative` keeps the caller's minus sign AND colours the figure
 * `--danger` ("The Sign-and-Colour Rule"): the sign is the meaning, the colour
 * repeats it. Pass the sign in the text; this component never adds or strips
 * one, because the server formats money.
 */
function Figure({
  className,
  negative = false,
  ...props
}: React.ComponentProps<'span'> & { negative?: boolean }) {
  return (
    <span
      data-slot="figure"
      data-negative={negative || undefined}
      dir="ltr"
      className={cn(
        'whitespace-nowrap [unicode-bidi:isolate]',
        negative && 'text-danger',
        className,
      )}
      {...props}
    />
  )
}

export {
  Figure,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
